import { FC, ReactNode, useContext, useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { observer } from 'mobx-react'
import './SignProgress.css'
import { cx } from '@/utils/cx'
import { StoreContext } from '@store/Root.store'
import { Document } from '@components/SignDocumentCommon/types'

interface SignProgressProps {
  children?: ReactNode
  currentPage: number
  documentList: Document[]
  setContainerHeight?: (value: number) => void
  showSubHeader?: boolean
}

export const SignProgress: FC<SignProgressProps> = observer(
  ({ children, currentPage, documentList, setContainerHeight, showSubHeader }) => {
    const store = useContext(StoreContext)
    const { signDocumentPage: trans } = store.TranslationsState.translations

    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (containerRef.current && documentList?.length) {
        setContainerHeight && setContainerHeight(containerRef?.current?.clientHeight + 10)
      }
    }, [containerRef.current, documentList])

    const docName = documentList?.at(currentPage - 1)?.name
    const progressPercent = Math.min(Math.max(currentPage / documentList?.length, 0), 1) * 100

    return (
      <div className="sign-progress__container" ref={containerRef}>
        <div
          className={cx(
            'sign-progress__grid',
            showSubHeader && 'sign-progress__grid--with-subtitle'
          )}
        >
          <div className="sign-progress__index">
            {trans.signProgress.document} {currentPage} {trans.signProgress.of}{' '}
            {documentList?.length}
          </div>

          {documentList?.length <= 5 ? (
            <div className="sign-progress__bars-container">
              {documentList.map((doc, index) => {
                return (
                  <div
                    key={doc.fileId}
                    className={cx(
                      'sign-progress__single-bar',
                      index + 1 <= currentPage && 'sign-progress__single-bar--active'
                    )}
                  />
                )
              })}
            </div>
          ) : (
            <div className="sign-progress__bar">
              <div
                className="sign-progress__filled-bar"
                style={{ '--sign-progress-fill': `${progressPercent}%` } as CSSProperties}
              />
            </div>
          )}

          <div className="sign-progress__title" data-testid="sign-progress-title">
            <div className="sign-progress__title-header">
              {trans.header}
              {!!docName && <span>:</span>}
            </div>

            <div
              className={cx(
                'sign-progress__title-doc-name',
                !docName && 'sign-progress__title-doc-name--hidden'
              )}
            >
              {docName}
            </div>
          </div>

          {showSubHeader && <div className="sign-progress__subtitle">{trans.subHeader}</div>}
        </div>

        <div className="sign-progress__zoom-controls">{children}</div>
      </div>
    )
  }
)
